import React, { useEffect, useState } from "react";
import { View, Text, Image as RNImage, Platform } from "react-native";
import ProfileDetails from "./Profiles/ProfileDetails";
import ProfileEditForm from "./Profiles/ProfileEditForm";

/**
 * Helper to upload a new profile image to API.
 * Returns new profile image ID if success.
 */
const uploadProfileImage = async (fileOrUri, client_id) => {
  let file = fileOrUri;
  if (typeof fileOrUri === "string" && fileOrUri.startsWith("file://")) {
    const resp = await fetch(fileOrUri);
    file = await resp.blob();
    file.name = "upload.jpg";
  }
  const formData = new FormData();
  formData.append("document", file);
  formData.append("entity", "client_profile_temp");
  formData.append("entity_id", String(client_id));
  try {
    const resp = await window.main.axios.post(window.main.api.uploadClientProfileImage, formData);
    console.log("uploadProfileImage raw response:", resp?.data);
    // This endpoint returns success + documentId
    if (resp?.data?.success && resp?.data?.documentId) {
      return resp.data.documentId;
    }
    // Keep these fallbacks just in case
    if (resp?.data?.profile_image_id) return resp.data.profile_image_id;
    if (resp?.data?.image_id) return resp.data.image_id;
    if (resp?.data?.data?.profile_image_id) return resp.data.data.profile_image_id;
    if (resp?.data?.data?.image_id) return resp.data.data.image_id;
    window.main.lib.setStatusState(`Image upload failed: ${resp?.data?.error || resp?.data?.message || "No documentId returned"}`);
    return null;
  } catch (err) {
    console.log("uploadProfileImage error:", err);
    window.main.lib.setStatusState(window.main.lib.parseErrorMessage(err));
    return null;
  }
};

const fetchClientProfile = async () => {
  const resp = await window.main.axios.get(window.main.api.getClientProfile);
  console.log("fetchClientProfile result:", resp.data);
  if (resp?.data?.client_id) {
    window.main.lib.setUserProfile(resp.data);
    return resp.data;
  }
  return null;
};
const buildProfileImageUrl = (profile_image_id) => {
  if (!profile_image_id || !window?.main?.api?.getClientProfileImage) {
    return null;
  }
  return (window.main.api.url+window.main.api.getClientProfileImage.replace("{profile_image_id}",profile_image_id)+"?cache="+Date.now());
};

const Profile = () => {
  const _theme = window.main.theme;

  // Add version state
  const [profileVersion, setProfileVersion] = useState(0);

  const [userProfileOrig, setUserProfile] = useState(window.main.lib.getUserProfile());

  // Use the latest data from userProfile
  //const userProfileOrig = window.main.lib.getUserProfile();
  const [form, setForm] = useState({
    first_name: userProfileOrig.first_name || "",
    last_name: userProfileOrig.last_name || "",
    email_1: userProfileOrig.email_1 || "",
    phone_1: String(userProfileOrig.phone_1 || "").replace(/\D/g, "").slice(0, 10),
  });

  const [profileImageUrl, setProfileImageUrl] = useState( buildProfileImageUrl(userProfileOrig.profile_image_id) || userProfileOrig.profile_image_url || null);
  const [pendingImageUrl, setPendingImageUrl] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});


  useEffect(() => {
    const refreshed = window.main.lib.getUserProfile();
    console.log("Profile useEffect: refreshed profile is", refreshed);

    setUserProfile(refreshed);
    setErrors({});
    setForm({
      first_name: refreshed.first_name || "",
      last_name: refreshed.last_name || "",
      email_1: refreshed.email_1 || "",
      phone_1: String(refreshed.phone_1 || "").replace(/\D/g, "").slice(0, 10),
    });

    if (refreshed.profile_image_id) {
      setProfileImageUrl(buildProfileImageUrl(refreshed.profile_image_id));
    } else if (refreshed.profile_image_url) {
      setProfileImageUrl(refreshed.profile_image_url);
    }
  }, [profileVersion]);

  // This is called after a successful profile update or image upload
  const refreshProfile = async () => {
    const updated = await fetchClientProfile();
    setProfileVersion(v => v + 1);

    if (updated?.profile_image_id) {
      setProfileImageUrl(buildProfileImageUrl(updated.profile_image_id));
      setPendingImageUrl(null);
    } else if (updated?.profile_image_url) {
      setProfileImageUrl(updated.profile_image_url);
      setPendingImageUrl(null);
    }

    if (updated) {
      setForm({
        first_name: updated.first_name || "",
        last_name: updated.last_name || "",
        email_1: updated.email_1 || "",
        phone_1: String(updated.phone_1 || "").replace(/\D/g, "").slice(0, 10),
      });
      setUserProfile(updated);
    }
  };

  const handleStartEdit = () =>  setEditing(true);
  //const handleChange = (field, value) => setForm((old) => ({ ...old, [field]: value }));
  const handleChange = (field, value) => {setForm((old) => ({ ...old, [field]: value }));setErrors((old) => ({ ...old, [field]: null }));};
  //const handleCancel = () => setEditing(false);
  const handleCancel = () => {setErrors({});setEditing(false);};

  const handleSubmit = async (payload) => {
    window.main.lib.setStatusState(null);

    const cleanedPhone = String(payload.phone_1 || "").replace(/\D/g, "").slice(0, 10);

    const nextErrors = {};

    if (!String(payload.first_name || "").trim()) {
      nextErrors.first_name = "First name is required.";
    }

    if (!String(payload.last_name || "").trim()) {
      nextErrors.last_name = "Last name is required.";
    }

    if (!String(payload.email_1 || "").trim()) {
      nextErrors.email_1 = "Email is required.";
    } else if (!window.main.lib.isValidEmail(payload.email_1)) {
      nextErrors.email_1 = "Enter a valid email address.";
    }

    if (!cleanedPhone) {
      nextErrors.phone_1 = "Phone is required.";
    } else if (cleanedPhone.length !== 10) {
      nextErrors.phone_1 = "Phone must have 10 digits.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      window.main.lib.setStatusState("Please fix the highlighted fields.");
      return;
    }

    setErrors({});

    const cleanedPayload = {
      ...payload,
      phone_1: cleanedPhone,
    };

    setLoading(true);
    try {
      window.main.lib.setLoadingState(true);
      const result = await window.main.axios.post(
        window.main.api.updateClientProfile,
        cleanedPayload
      );

      if (result.data?.success) {
        await refreshProfile();
        setEditing(false);
        window.main.lib.setStatusState("Profile updated.");
      } else {
        window.main.lib.setStatusState(result.data?.error || "Failed to update profile.");
      }
    } catch (err) {
      window.main.lib.setStatusState(window.main.lib.parseErrorMessage(err));
    } finally {
      setLoading(false);
      window.main.lib.setLoadingState(false);
    }
  };

  // Now, update global state and local url when image is uploaded!
  const handleImageChange = async (fileOrUri, localPreviewUrl = null) => {
    const currentProfile = window.main.lib.getUserProfile();
    const client_id = currentProfile.client_id;
    const previousImageUrl = displayedImageUrl;

    console.log("handleImageChange started", { client_id, localPreviewUrl });

    if (localPreviewUrl) {
      setPendingImageUrl(localPreviewUrl);
      setProfileImageUrl(localPreviewUrl);
    }

    try {
      window.main.lib.setLoadingState(true);

      // 1) Upload to temp
      const uploadedDocumentId = await uploadProfileImage(fileOrUri, client_id);
      console.log("uploadProfileImage returned document id:", uploadedDocumentId);

      if (!uploadedDocumentId) {
        console.log("No document id returned from uploadProfileImage.");
        setPendingImageUrl(null);
        setProfileImageUrl(previousImageUrl);
        window.main.lib.setStatusState("Image upload failed.");
        return;
      }

      // 2) Immediately save profile so temp image becomes real profile image
      const payload = {
        client_id,
        first_name: form.first_name || currentProfile.first_name || "",
        last_name: form.last_name || currentProfile.last_name || "",
        email_1: form.email_1 || currentProfile.email_1 || "",
        phone_1: String(form.phone_1 || currentProfile.phone_1 || "").replace(/\D/g, "").slice(0, 10),
      };

      console.log("Calling updateClientProfile after image upload with payload:", payload);

      const result = await window.main.axios.post(
        window.main.api.updateClientProfile,
        payload
      );

      console.log("updateClientProfile result after image upload:", result?.data);

      if (result?.data?.success) {
        window.main.lib.setStatusState("Image updated!");
        await refreshProfile();
      } else {
        setPendingImageUrl(null);
        setProfileImageUrl(previousImageUrl);
        window.main.lib.setStatusState(result?.data?.error || "Failed to save image.");
      }
    } catch (err) {
      console.log("handleImageChange error:", err);
      setPendingImageUrl(null);
      setProfileImageUrl(previousImageUrl);
      window.main.lib.setStatusState(window.main.lib.parseErrorMessage(err));
    } finally {
      window.main.lib.setLoadingState(false);
    }
  };

  // Always uses latest state!
  const displayedImageUrl = pendingImageUrl || profileImageUrl;
  const userProfile = {...userProfileOrig, profile_image_url:displayedImageUrl};

  return (
    <View style={{alignItems:"flex-start",display:"flex",flexDirection:"column",justifyContent:"flex-start",paddingHorizontal:24,paddingTop:30,width:"100%"}}>
      <View style={{width:"100%",maxWidth:325}}>
        {/* Title */}
        <View style={{ marginBottom: 15 }}>
          <Text style={{ ..._theme.pageTitleText, fontWeight: "bold", marginBottom: 0 }}>
            My Profile
          </Text>
        </View>

        {/* Back Link */}
        <View style={{ marginBottom: 10 }}>
          <Text style={{color:"#33aa77",marginBottom:8,fontWeight:"bold",minWidth:60,fontSize:16,cursor:"pointer"}} onPress={() => window.main.lib.setCurrentView("home")} accessibilityRole="button">
            {"← Back to My Pets"}
          </Text>
        </View>

        {/* Card */}
        <View style={{..._theme.containerLiner,alignSelf: "stretch",backgroundColor:_theme.colors.mode.containerBackgroundColor,borderRadius:_theme.borderRadius,marginBottom:30,marginHorizontal:0,marginLeft:0,marginRight:0,minWidth:300,padding:24,position:"relative",width:"100%"}}>
          {editing ? (
            <ProfileEditForm form={form} errors={errors} onChange={handleChange} onCancel={handleCancel} onSubmit={handleSubmit} onImageChange={handleImageChange} profile={userProfile} imageUrl={displayedImageUrl} theme={_theme} loading={loading} />
          ) : (
            <ProfileDetails profile={userProfile} imageUrl={displayedImageUrl} onEdit={handleStartEdit} onImageChange={handleImageChange} theme={_theme} />
          )}
        </View>
      </View>
    </View>
  );
};

export default Profile;